import { useState, useRef, useEffect } from "react";
import { useTheme, DARK, LIGHT } from "./Layout";
import { ChevronDown, Search, X } from "lucide-react";

interface Option {
  value: string | number;
  label: string;
}

interface Props {
  options: Option[];
  value: string | number;
  onChange: (val: string) => void;
  placeholder?: string;
  label?: string;
  required?: boolean;
  style?: React.CSSProperties;
  tabIndex?: number;
}

export default function SearchableSelect({ options, value, onChange, placeholder = "— انتخاب کنید", label, required, style, tabIndex }: Props) {
  const { dark } = useTheme();
  const T = dark ? DARK : LIGHT;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = options.find(o => String(o.value) === String(value));

  // بستن با کلیک بیرون
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // فوکوس روی جستجو بعد از باز شدن
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  const filtered = query
    ? options.filter(o => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;

  // ESC و Tab در dropdown
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setOpen(false);
      setQuery("");
      triggerRef.current?.focus();
    }
    if (e.key === "Tab") {
      setOpen(false);
      setQuery("");
    }
  };

  // Tab روی trigger باز میکنه با keyboard
  const handleTriggerKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setOpen(o => !o);
    }
    if (e.key === "Escape") {
      setOpen(false);
      setQuery("");
    }
    if (e.key === "ArrowDown" && !open) {
      e.preventDefault();
      setOpen(true);
    }
  };

  const inp: React.CSSProperties = {
    width: "100%", padding: "9px 12px",
    background: dark ? "rgba(255,255,255,0.05)" : "#f8faff",
    border: `1.5px solid ${open ? "#3b82f6" : dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
    borderRadius: 8, color: T.text, fontSize: 13,
    fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
    direction: "rtl", outline: "none", boxSizing: "border-box",
    cursor: "pointer", ...style,
  };

  return (
    <div ref={ref} style={{ position: "relative", width: "100%" }}>
      {label && (
        <label style={{ fontSize: 11, fontWeight: 700, color: T.subtext, display: "block", marginBottom: 4 }}>
          {label}{required && " *"}
        </label>
      )}

      {/* trigger */}
      <div
        ref={triggerRef}
        tabIndex={tabIndex ?? 0}
        role="combobox"
        aria-expanded={open}
        onClick={() => { setOpen(o => !o); setQuery(""); }}
        onKeyDown={handleTriggerKeyDown}
        style={{ ...inp, display: "flex", alignItems: "center", justifyContent: "space-between", userSelect: "none", outline: "none" }}
      >
        <span style={{ color: selected ? T.text : T.subtext, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {selected ? selected.label : placeholder}
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
          {value && (
            <div
              onClick={e => { e.stopPropagation(); onChange(""); setQuery(""); }}
              style={{ cursor: "pointer", color: T.subtext, display: "flex", alignItems: "center" }}
            >
              <X size={12} />
            </div>
          )}
          <ChevronDown size={13} color={T.subtext} style={{ transition: "transform 0.2s", transform: open ? "rotate(180deg)" : "rotate(0deg)" }} />
        </div>
      </div>

      {/* dropdown */}
      {open && (
        <div style={{
          position: "absolute", top: "100%", right: 0, left: 0, zIndex: 9999, marginTop: 4,
          background: dark ? "#1e293b" : "#fff",
          border: `1.5px solid ${dark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.1)"}`,
          borderRadius: 10, boxShadow: "0 8px 32px rgba(0,0,0,0.2)", overflow: "hidden",
        }}>
          {/* جستجو */}
          <div style={{ padding: "8px 10px", borderBottom: `1px solid ${dark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.06)"}`, position: "relative" }}>
            <Search size={12} style={{ position: "absolute", right: 20, top: "50%", transform: "translateY(-50%)", color: T.subtext }} />
            <input
              ref={inputRef}
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="جستجو..."
              style={{
                width: "100%", padding: "7px 32px 7px 10px",
                background: dark ? "rgba(255,255,255,0.05)" : "#f8faff",
                border: `1px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
                borderRadius: 7, color: T.text, fontSize: 12,
                fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
                direction: "rtl", outline: "none", boxSizing: "border-box",
              }}
            />
          </div>

          {/* آیتم‌ها */}
          <div style={{ maxHeight: 220, overflowY: "auto", scrollbarWidth: "none" }}>
            <div
              onClick={() => { onChange(""); setOpen(false); setQuery(""); }}
              style={{ padding: "9px 14px", fontSize: 12, color: T.subtext, cursor: "pointer", borderBottom: `1px solid ${dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)"}` }}
              onMouseEnter={e => (e.currentTarget.style.background = dark ? "rgba(255,255,255,0.05)" : "#f0f4ff")}
              onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
            >
              {placeholder}
            </div>
            {filtered.length === 0 ? (
              <div style={{ padding: "16px", textAlign: "center", color: T.subtext, fontSize: 12 }}>نتیجه‌ای یافت نشد</div>
            ) : (
              filtered.map(opt => (
                <div
                  key={opt.value}
                  onClick={() => { onChange(String(opt.value)); setOpen(false); setQuery(""); triggerRef.current?.focus(); }}
                  style={{
                    padding: "9px 14px", fontSize: 12, cursor: "pointer",
                    color: String(opt.value) === String(value) ? "#3b82f6" : T.text,
                    fontWeight: String(opt.value) === String(value) ? 700 : 400,
                    background: String(opt.value) === String(value) ? (dark ? "rgba(59,130,246,0.12)" : "rgba(59,130,246,0.07)") : "transparent",
                    borderBottom: `1px solid ${dark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.03)"}`,
                  }}
                  onMouseEnter={e => { if (String(opt.value) !== String(value)) e.currentTarget.style.background = dark ? "rgba(255,255,255,0.05)" : "#f0f4ff"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = String(opt.value) === String(value) ? (dark ? "rgba(59,130,246,0.12)" : "rgba(59,130,246,0.07)") : "transparent"; }}
                >
                  {opt.label}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}